import React, { useState, useEffect, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { formatCurrency } from '../shared/utils'
import EditOrderItem from './Modals/EditOrderItem'
import AlternativesModal from '../Recommendations/AlternativesModal'
import { InputNumber } from 'primereact/inputnumber'
import { AuthenticationContext } from '../../Auth/authentication.context'

const DEFAULT_LAZY_STATE = { first: 0, rows: 10, page: 0 }

const SearchResults = ({
  results,
  lazyState = DEFAULT_LAZY_STATE,
  rowsPerPageOptions = [5, 10, 25, 50],
  onLazyPage = () => {},
  totalCount,
  handleMyOrder,
  tableLoading,
  editItem,
  handleResultsUpdate,
  handleKeyPress
}) => {
  const { user } = useContext(AuthenticationContext)
  const isGroupMember = !!(user?.pharmacy?.pharmacyGroup || user?.pharmacyGroup || user?.pharmacyGroupId)

  const lineBlockedFromOrdering = (rowData) =>
    rowData.orderable === false || rowData.backlog === true

  /** Shown on hover over Add to order (wrapper handles disabled buttons). */
  const addToOrderTooltip = (rowData) => {
    if (rowData.backlog === true) {
      return 'Ordering is disabled because this item is on backorder.'
    }
    if (rowData.orderable === false) {
      return 'This product is not available to order from this manufacturer.'
    }
    if (!rowData.tempQuantity) {
      return 'Enter a quantity before adding this item to your order.'
    }
    return undefined
  }
  const [isEditModalVisible, setEditModalVisible] = useState(false)
  const [alternativesVisible, setAlternativesVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [rowItemData, setRowItemData] = useState({})
  const [isAddFromAltModalVisible, setAddFromAltModalVisible] = useState(false)
  const [productToAdd, setProductToAdd] = useState({})
  const [localResults, setLocalResults] = useState([])

  const rowClassName = (rowData) => {
    return rowData.orderable === false ? 'gray-background' : ''
  }

  const strengthBodyTemplate = (rowData) => {
    // Try to extract strength from itemDescription if strength field doesn't exist
    let strength = rowData.strength || rowData.drugStrength;
    if (!strength && rowData.itemDescription) {
      const strengthMatch = rowData.itemDescription.match(/\[Strength:\s*([^\]]+)\]/i);
      if (strengthMatch) {
        strength = strengthMatch[1].trim();
      }
    }
    return (
      <div>
        {strength || 'N/A'}
      </div>
    )
  }
  
  const handleAddToCartFromAlternatives = (product) => {
    setProductToAdd({
      ...product,
      quantity: 1,
      subtotal: product.sellingPrice || 0,
    })
    setAddFromAltModalVisible(true)
  }

  const handleCheckAlternatives = (product) => {
    setSelectedProduct(product)
    setAlternativesVisible(true)
  }

  const alternativesBodyTemplate = (rowData) => {
    return (
      <div className='d-flex justify-content-center'>
        <Button
          type='button'
          label='Alternatives'
          outlined
          onClick={() => handleCheckAlternatives(rowData)}
          style={{
            color: '#2196F3',
            borderColor: '#2196F3'
          }}
        />
      </div>
    )
  }

  const actionBodyTemplate = (rowData) => {
    const blocked = lineBlockedFromOrdering(rowData)
    const disabled = blocked || !rowData.tempQuantity
    const tip = disabled ? addToOrderTooltip(rowData) : undefined
    return (
      <div className='d-flex justify-content-center'>
        {!rowData.quantity ? (
          <span
            className='d-inline-block'
            title={tip || undefined}
            style={{ cursor: disabled ? 'not-allowed' : undefined }}
          >
            <Button
              disabled={disabled}
              type='button'
              label='Add to order'
              className={`text-center${rowData.backlog ? ' opacity-50' : ''}`}
              onClick={() => {
                if (!rowData.tempQuantity || blocked) return

                const orderDetails = {
                  ...rowData,
                  quantity: rowData.tempQuantity,
                  subtotal: rowData.tempQuantity * rowData.sellingPrice
                }
                handleMyOrder(orderDetails)
              }}
            />
          </span>
        ) : (
          <Button
            icon='pi pi-pencil'
            rounded
            outlined
            className='mr-2'
            onClick={() => {
              setEditModalVisible(true)
              setRowItemData(rowData)
            }}
          />
        )}
      </div>
    )
  }

  useEffect(() => {
    if (results) {
      const updatedResults = results.map(item => ({
        ...item,
        tempQuantity: item.quantity || item.tempQuantity || 1
      }))
      setLocalResults(updatedResults)
    }
  }, [results])

  const handlePageChange = (e) => {
    onLazyPage({
      first: e.first,
      rows: e.rows,
      page: e.page,
    })
  }

  const handleQuantityChange = (rowData, newValue) => {
    const updatedResults = localResults.map(item => {
      if (item.id === rowData.id) {
        return {
          ...item,
          tempQuantity: newValue
        }
      }
      return item
    })
    setLocalResults(updatedResults)
    handleResultsUpdate(updatedResults)
  }

  return (
    <>
      <DataTable
        value={localResults}
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        totalRecords={totalCount || 0}
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={lazyState?.rows ?? DEFAULT_LAZY_STATE.rows}
        paginator
        lazy
        first={lazyState?.first ?? DEFAULT_LAZY_STATE.first}
        onPage={(e) => {
          handlePageChange(e);
        }}
        showGridlines
        loading={tableLoading}
        dataKey='id'
        removableSort
        emptyMessage={() => {
          return (
            <div
              style={{
                height: '365px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontWeight: '400' }}>
                {tableLoading 
                  ? 'Loading...' 
                  : localResults?.length === 0 
                    ? 'No results found' 
                    : 'Use the search bar to find products'
                }
              </span>
            </div>
          )
        }}
      >
        <Column field='retailUpcCode' header='UPC#' />
        <Column field='itemDescription' header='Product Description' />
        <Column field='manufacturer.name' header='Manufacturer' />
        <Column 
          field='drugIdentificationNo' 
          header='DIN/NPN'
          body={(rowData) => {
            const din = rowData.drugIdentificationNo
            if (din == null || din === '') return din
            return String(din).padStart(8, '0')
          }}
        />
        <Column header='Strength' body={strengthBodyTemplate} />
        <Column header='Price' body={(e) => formatCurrency(e.sellingPrice)} />
        {isGroupMember && (
          <Column body={alternativesBodyTemplate} header='PA Navigator' style={{ minWidth: '180px' }} />
        )}
        <Column
          header='Quantity'
          body={(rowData) => (
            <InputNumber
              value={rowData.tempQuantity}
              onValueChange={(e) => handleQuantityChange(rowData, e.value)}
              mode='decimal'
              showButtons
              min={0}
              disabled={lineBlockedFromOrdering(rowData) || rowData.quantity}
              placeholder='Enter Quantity'
              useGrouping={false}
              minFractionDigits={0}
              maxFractionDigits={0}
              onKeyPress={(e) => handleKeyPress && handleKeyPress(e, rowData)}
            />
          )}
          style={{ minWidth: '150px' }}
        />
        <Column body={actionBodyTemplate} style={{ minWidth: '200px' }} />
      </DataTable>
      <EditOrderItem
        visible={isEditModalVisible}
        setVisible={setEditModalVisible}
        data={rowItemData}
        editItem={editItem}
        originalLineQuantity={rowItemData.quantity}
      />
      {isGroupMember && (
        <AlternativesModal
          visible={alternativesVisible}
          setVisible={setAlternativesVisible}
          product={selectedProduct}
          includeOdPaAdvantage={['Origins Direct', 'Origins Direct Instant'].includes(localStorage.getItem('module') || '')}
          onAddToCart={handleAddToCartFromAlternatives}
        />
      )}
      <EditOrderItem
        visible={isAddFromAltModalVisible}
        setVisible={setAddFromAltModalVisible}
        data={productToAdd}
        originalLineQuantity={null}
        editItem={(updatedData) => {
          handleMyOrder({ ...updatedData, tempQuantity: updatedData.quantity })
        }}
      />
    </>
  )
}

export default SearchResults
